// App
import type React from 'react';
import { yieldCallback } from '@zillow/yield-callback';

// Hooks
import { usePageLinks } from './hooks';

// Misc /utils
import routeUtils from 'app/shared/utils/routeUtils';
import { PrevNextLinker } from './styles';

type PaginationPrevNextProps = {
  areaResourceId?: string;
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
  ariaLabel?: string;
  children?: React.ReactNode;
  isBold?: boolean;
  isDisabled?: boolean;
  linkToPage?: number;
  linkType?: string;
  isActive?: boolean;
  onPaginationItemClick?: () => void;
  query?: Record<string, string>;
  searchSlug?: string;
};

const PaginationPrevNext = ({
  areaResourceId,
  ariaCurrent,
  ariaLabel,
  children,
  isBold,
  isDisabled = false,
  linkToPage,
  linkType = 'accent',
  isActive = false,
  onPaginationItemClick,
  query,
  searchSlug,
}: PaginationPrevNextProps) => {
  const [makeLink] = usePageLinks();
  const pageLink = makeLink(linkToPage, query, routeUtils.buildAreaPath({ areaResourceId, searchSlug }));

  const yieldCallbackAndTransition = yieldCallback(() => {
    onPaginationItemClick?.();
    window.router.transitionTo(pageLink);
  });

  const handlePrevNextClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Prevent the browser from going through with its default <a> behavior
    e.preventDefault();

    yieldCallbackAndTransition();
  };

  return (
    <PrevNextLinker
      tabIndex={isDisabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
      aria-disabled={isDisabled}
      disabled={isDisabled}
      isActive={isActive}
      isBold={isBold}
      onClick={handlePrevNextClick}
      linkType={linkType}
      to={isDisabled ? undefined : pageLink}
    >
      {children}
    </PrevNextLinker>
  );
};

export default PaginationPrevNext;
